import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import { useRouter } from 'next/router';
import { useGetSingleEntitySchema, generateSingleItemQuery } from '@blaze-cms/react-page-builder';
import { MainContext } from '@blaze-cms/nextjs-components';
import RegistrationForm from './RegistrationForm';
import { GET_COUNTRIES, GET_ORGANIZATION_TYPES } from '../../services/querys';
import { FORM_SCHEMA } from './constants';
import buildSelectOptions from '../helpers/build-select-options';

const RegistrationFormFactory = ({
  redirectUrl,
  parent,
  subscriptionTypeId,
  subscriptionId: settingsSubscriptionId,
  privacyPolicyUrl,
  ...props
}) => {
  const { isPreview } = useContext(MainContext);
  const { itemId, itemEntity } = parent;
  const { data: { getEntitySchema = {} } = {}, loading: schemaLoading } =
    useGetSingleEntitySchema(itemEntity);
  const { actions: { getPublished, get } = {} } = getEntitySchema;
  const query = useMemo(() => {
    const getAction = isPreview ? get : getPublished;
    return generateSingleItemQuery(getAction, 'id,subscriptionId');
  }, [isPreview, get, getPublished]);
  const { data: { entityData = {} } = {}, loading: itemQueryLoading } = useQuery(query, {
    variables: { id: itemId },
    skip: subscriptionTypeId || settingsSubscriptionId || schemaLoading
  });
  const router = useRouter();
  const { isLoggedIn } = checkIfLoggedIn();
  const { data: { countries = [] } = {}, loading: countriesLoading } = useQuery(GET_COUNTRIES, {
    skip: isLoggedIn
  });
  const { data: { organizationTypes = [] } = {}, loading: organizationTypesLoading } = useQuery(
    GET_ORGANIZATION_TYPES,
    {
      skip: isLoggedIn
    }
  );

  if (isLoggedIn) {
    router.push(redirectUrl || '/');
    return null;
  }
  if (countriesLoading || organizationTypesLoading || schemaLoading || itemQueryLoading)
    return null;
  const countryOptions = buildSelectOptions(countries);
  const organizationTypeOptions = buildSelectOptions(organizationTypes);
  const initialSchema = FORM_SCHEMA(privacyPolicyUrl);
  initialSchema.properties.country.options = countryOptions;
  initialSchema.properties.country.enum =
    countryOptions.length > 0 ? countryOptions.map(([v, _]) => v) : undefined;
  initialSchema.properties.organizationTypeId.options = organizationTypeOptions;
  initialSchema.properties.organizationTypeId.enum =
    organizationTypeOptions.length > 0 ? organizationTypeOptions.map(([v, _]) => v) : undefined;

  let subscriptionId = settingsSubscriptionId;
  if (!subscriptionTypeId && !settingsSubscriptionId) {
    const { subscriptionId: recordSubscriptionId } = entityData;
    subscriptionId = recordSubscriptionId;
  }

  return (
    <RegistrationForm
      {...props}
      subscriptionId={subscriptionId}
      subscriptionTypeId={subscriptionTypeId}
      redirectUrl={redirectUrl}
      initialSchema={initialSchema}
      countries={countries}
      organizationTypes={organizationTypes}
    />
  );
};

RegistrationFormFactory.propTypes = {
  redirectUrl: PropTypes.string,
  parent: PropTypes.object.isRequired,
  subscriptionTypeId: PropTypes.string.isRequired,
  subscriptionId: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string
};

RegistrationFormFactory.defaultProps = {
  redirectUrl: null,
  privacyPolicyUrl: '/privacy-policy'
};

export default RegistrationFormFactory;
